var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { toSameOriginGo2rtcUrl } from './go2rtcUrl';
/** Codecs go2rtc / video-rtc typically negotiate for MSE. */
const MSE_CODECS = ['avc1.640029', 'avc1.64002A', 'avc1.640033', 'hvc1.1.6.L153.B0', 'hvc1.1.6.L120.B0', 'hvc1.1.6.L93.B0', 'hev1.1.6.L153.B0', 'hev1.1.6.L120.B0', 'hev1.1.6.L93.B0', 'mp4a.40.2', 'flac', 'opus'];
const MAX_WS_RETRIES = 2;
const RETRY_BASE_MS = 400;
function isSourceBufferLive(sb, ms) {
    if (!sb || !ms || ms.readyState !== 'open') {
        return false;
    }
    try {
        void sb.buffered;
        return true;
    }
    catch (_a) {
        return false;
    }
}
/**
 * go2rtc MSE player: WebSocket `/api/ws?src=…` → MediaSource fMP4 (same protocol as video-rtc).
 * Retries WS a couple of times when go2rtc closes before the first frame (cold RTSP / channel switch).
 */
export function createMsePlayer(playback) {
    let video = null;
    let ws = null;
    let mediaSource = null;
    let objectUrl = null;
    let closed = false;
    let session = 0;
    let retryTimer = null;
    let ttffLogged = false;
    let wsRetries = 0;
    const listeners = {
        MEDIA_INFO: [],
        ERROR: [],
    };
    const emit = (event, ...args) => {
        if (closed && event === 'ERROR') {
            return;
        }
        (listeners[event] || []).forEach((listener) => listener(...args));
    };
    const markFirstFrame = () => {
        if (ttffLogged || closed) {
            return;
        }
        ttffLogged = true;
        emit('MEDIA_INFO');
    };
    const clearRetryTimer = () => {
        if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
    };
    const detachSocket = () => {
        const dying = ws;
        ws = null;
        if (!dying) {
            return;
        }
        dying.onopen = null;
        dying.onmessage = null;
        dying.onerror = null;
        dying.onclose = null;
        try {
            dying.close();
        }
        catch (_a) {
            // ignore
        }
    };
    const detachMedia = () => {
        if (video) {
            try {
                video.removeAttribute('src');
                video.srcObject = null;
                video.load();
            }
            catch (_a) {
                // ignore
            }
        }
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
        }
        mediaSource = null;
    };
    const cleanup = () => {
        session += 1;
        clearRetryTimer();
        detachSocket();
        detachMedia();
    };
    const handle = {
        attachMediaElement(element) {
            video = element;
            video.muted = true;
            video.autoplay = true;
            video.playsInline = true;
        },
        detachMediaElement() {
            cleanup();
            video = null;
        },
        load() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!video || closed) {
                    return;
                }
                cleanup();
                ttffLogged = false;
                wsRetries = 0;
                const MediaSourceCtor = typeof window !== 'undefined' ? window.MediaSource || window.ManagedMediaSource : undefined;
                if (!MediaSourceCtor) {
                    emit('ERROR', 'MEDIA_ERROR');
                    return;
                }
                const httpUrl = toSameOriginGo2rtcUrl(playback.url);
                const wsUrl = httpUrl.replace(/^http/i, 'ws');
                const connect = () => {
                    if (closed || !video) {
                        return;
                    }
                    clearRetryTimer();
                    detachSocket();
                    detachMedia();
                    const mySession = session;
                    const socket = new WebSocket(wsUrl);
                    ws = socket;
                    socket.binaryType = 'arraybuffer';
                    let sourceBuffer = null;
                    const pending = [];
                    let pendingLen = 0;
                    const stillActive = () => !closed && session === mySession && ws === socket;
                    const scheduleRetryOrFail = (reason) => {
                        if (!stillActive() || ttffLogged) {
                            return;
                        }
                        if (wsRetries < MAX_WS_RETRIES) {
                            wsRetries += 1;
                            const delay = RETRY_BASE_MS * wsRetries;
                            detachSocket();
                            detachMedia();
                            retryTimer = setTimeout(() => {
                                if (!closed && session === mySession) {
                                    connect();
                                }
                            }, delay);
                            return;
                        }
                        emit('ERROR', reason === 'media' ? 'MEDIA_ERROR' : 'NETWORK_ERROR');
                    };
                    const flushPending = () => {
                        if (!stillActive() || !isSourceBufferLive(sourceBuffer, mediaSource) || pendingLen === 0) {
                            return;
                        }
                        if (sourceBuffer.updating) {
                            return;
                        }
                        const merged = new Uint8Array(pendingLen);
                        let offset = 0;
                        pending.forEach((chunk) => {
                            merged.set(chunk, offset);
                            offset += chunk.byteLength;
                        });
                        pending.length = 0;
                        pendingLen = 0;
                        try {
                            sourceBuffer.appendBuffer(merged);
                        }
                        catch (_a) {
                            pendingLen = 0;
                        }
                    };
                    const trimAndCatchUp = () => {
                        if (!stillActive() || !video || !isSourceBufferLive(sourceBuffer, mediaSource)) {
                            return;
                        }
                        if (sourceBuffer.updating) {
                            return;
                        }
                        try {
                            if (!sourceBuffer.buffered.length) {
                                return;
                            }
                            const end = sourceBuffer.buffered.end(sourceBuffer.buffered.length - 1);
                            const start = Math.max(end - 5, sourceBuffer.buffered.start(0));
                            if (video.currentTime < start) {
                                video.currentTime = start;
                            }
                        }
                        catch (_a) {
                            // detached — ignore
                        }
                    };
                    const attachSourceBuffer = (mime, ms) => {
                        if (!stillActive() || ms.readyState !== 'open') {
                            return;
                        }
                        try {
                            sourceBuffer = ms.addSourceBuffer(mime);
                            sourceBuffer.mode = 'segments';
                            sourceBuffer.addEventListener('updateend', () => {
                                if (!stillActive()) {
                                    return;
                                }
                                flushPending();
                                trimAndCatchUp();
                            });
                        }
                        catch (_a) {
                            scheduleRetryOrFail('media');
                        }
                    };
                    socket.onopen = () => {
                        var _a;
                        if (!stillActive() || !video) {
                            return;
                        }
                        mediaSource = new MediaSourceCtor();
                        const ms = mediaSource;
                        ms.addEventListener('sourceopen', () => {
                            if (!stillActive() || !ws || ws.readyState !== WebSocket.OPEN) {
                                return;
                            }
                            const supported = MSE_CODECS.filter((codec) => MediaSourceCtor.isTypeSupported(`video/mp4; codecs="${codec}"`)).join(',');
                            try {
                                ws.send(JSON.stringify({ type: 'mse', value: supported }));
                            }
                            catch (_a) {
                                scheduleRetryOrFail('network');
                            }
                        }, { once: true });
                        const managed = 'ManagedMediaSource' in window && ms instanceof window.ManagedMediaSource;
                        if (managed) {
                            video.disableRemotePlayback = true;
                            video.srcObject = ms;
                        }
                        else {
                            objectUrl = URL.createObjectURL(ms);
                            video.src = objectUrl;
                            video.srcObject = null;
                        }
                        video.addEventListener('loadeddata', () => markFirstFrame(), { once: true });
                        video.addEventListener('playing', () => markFirstFrame(), { once: true });
                        (_a = video.play()) === null || _a === void 0 ? void 0 : _a.catch(() => undefined);
                    };
                    socket.onmessage = (event) => {
                        if (!stillActive()) {
                            return;
                        }
                        if (typeof event.data === 'string') {
                            let msg;
                            try {
                                msg = JSON.parse(event.data);
                            }
                            catch (_a) {
                                return;
                            }
                            if (msg.type === 'error') {
                                scheduleRetryOrFail('network');
                                return;
                            }
                            if (msg.type !== 'mse' || !msg.value || !mediaSource || mediaSource.readyState !== 'open') {
                                return;
                            }
                            attachSourceBuffer(msg.value, mediaSource);
                            return;
                        }
                        const chunk = new Uint8Array(event.data);
                        if (!isSourceBufferLive(sourceBuffer, mediaSource) || sourceBuffer.updating || pendingLen > 0) {
                            pending.push(chunk);
                            pendingLen += chunk.byteLength;
                            return;
                        }
                        try {
                            sourceBuffer.appendBuffer(chunk);
                        }
                        catch (_b) {
                            pending.push(chunk);
                            pendingLen += chunk.byteLength;
                        }
                    };
                    socket.onerror = () => {
                        // onclose usually follows; avoid double-fail. Only schedule if close never comes.
                    };
                    socket.onclose = () => {
                        if (!stillActive() || ttffLogged) {
                            return;
                        }
                        scheduleRetryOrFail('network');
                    };
                };
                try {
                    connect();
                }
                catch (_a) {
                    if (!closed) {
                        emit('ERROR', 'MEDIA_ERROR');
                    }
                }
            });
        },
        unload() {
            closed = true;
            cleanup();
        },
        pause() {
            video === null || video === void 0 ? void 0 : video.pause();
        },
        on(event, callback) {
            if (!listeners[event]) {
                listeners[event] = [];
            }
            listeners[event].push(callback);
        },
        off() {
            Object.keys(listeners).forEach((key) => {
                listeners[key] = [];
            });
        },
    };
    return handle;
}
let mseHevcCache = null;
/** Best-effort: can MSE accept an HEVC fMP4 SourceBuffer? */
export function canAttemptHevcMse() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!mseHevcCache) {
            mseHevcCache = Promise.resolve().then(() => {
                const MediaSourceCtor = typeof window !== 'undefined' ? window.MediaSource || window.ManagedMediaSource : undefined;
                if (!MediaSourceCtor || typeof MediaSourceCtor.isTypeSupported !== 'function') {
                    return false;
                }
                return MSE_CODECS.filter((c) => /hvc1|hev1/i.test(c)).some((codec) => MediaSourceCtor.isTypeSupported(`video/mp4; codecs="${codec}"`));
            });
        }
        return mseHevcCache;
    });
}
